'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '@/hooks/useAnalytics';
import Image from 'next/image';

// Sample app screenshots - replace with actual images later
const appScreenshots = [
  {
    id: 1,
    url: '/images/app-screens/community.jpg',
    alt: 'Sketchplay Community',
    title: 'Community',
    description: 'Create unlimited personalized conversations with everyone in your ecosystem—coaches, parents, players, and team members—all in one place.'
  },
  {
    id: 2,
    url: '/images/app-screens/teams.jpg',
    alt: 'Sketchplay Teams',
    title: 'Teams',
    description: 'Easily create, manage, and organize and verify your players directly in the platform.'
  },
  {
    id: 3,
    url: '/images/app-screens/regsiter.jpg',
    alt: 'Sketchplay Registration',
    title: 'Event Registration',
    description: 'Easily register for events, checkout with discounts, and manage your registrations with a quick tap of a button.'
  },
  {
    id: 4,
    url: '/images/app-screens/communication.jpg',
    alt: 'Sketchplay Communication',
    title: 'Communication',
    description: 'Keep everyone on the same page with Push Notifications, SMS, and Email Notifications.'
  },
  {
    id: 5,
    url: '/images/app-screens/purchase-history.jpg',
    alt: 'Sketchplay Purchase History',
    title: 'Purchase History',
    description: 'View your purchase history and manage your subscriptions.'
  }
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};

const phoneVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 20,
      delay: 0.2
    }
  }
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  })
};

const MobileShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { trackFeature } = useAnalytics();
  const [isMounted, setIsMounted] = useState(false);

  const navigate = useCallback((direction: 'prev' | 'next') => {
    if (direction === 'next') {
      setCurrentIndex((prev) => (prev + 1) % appScreenshots.length);
      trackFeature('mobile_showcase', 'next_slide_click');
    } else {
      setCurrentIndex((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);
      trackFeature('mobile_showcase', 'prev_slide_click');
    }
  }, [trackFeature]);

  useEffect(() => {
    setIsMounted(true);
    
    if (isAutoplay) {
      const interval = setInterval(() => {
        navigate('next');
      }, 5000);
      intervalRef.current = interval;
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoplay, navigate]);

  // Pause autoplay on hover
  const handleMouseEnter = () => setIsAutoplay(false);
  const handleMouseLeave = () => setIsAutoplay(true);

  return (
    <section className="relative py-24 overflow-hidden bg-futuristic-black">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-blue/10 via-futuristic-purple/5 to-transparent opacity-70"></div>
        
        {/* Animated particles */}
        {isMounted && [...Array(20)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute rounded-full"
            style={{
              width: `${2 + (i % 4)}px`,
              height: `${2 + (i % 4)}px`,
              top: `${(i * 5) % 100}%`,
              left: `${(i * 7) % 100}%`,
              background: i % 2 === 0 ? 'rgba(52, 152, 219, 0.7)' : 'rgba(155, 89, 182, 0.7)',
              boxShadow: i % 2 === 0 
                ? '0 0 10px 2px rgba(52, 152, 219, 0.5)' 
                : '0 0 10px 2px rgba(155, 89, 182, 0.5)'
            }}
            animate={{
              y: [0, -100 - (i % 50)],
              x: [0, ((i % 10) - 5) * 5],
              opacity: [0.7, 0],
              scale: [1, 0.5 + (i % 5) / 10]
            }}
            transition={{
              duration: 15 + (i % 10),
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-futuristic-blue/20 blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-futuristic-purple/20 blur-[100px]" />
      </div>

      <div className="container relative z-10 px-4 mx-auto">
        <motion.div
          className="flex flex-col items-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section header */}
          <motion.h2 
            className="mb-4 text-4xl font-bold text-center md:text-5xl"
            variants={itemVariants}
          >
            <span className="bg-gradient-to-r from-futuristic-blue to-futuristic-purple bg-clip-text text-transparent">
              Experience the App
            </span>
          </motion.h2>
          
          <motion.p 
            className="max-w-2xl mb-16 text-lg text-center text-gray-300"
            variants={itemVariants}
          >
            Discover how our mobile app transforms your sports management experience with these powerful features
          </motion.p>

          {/* Phone mockup with carousel */}
          <div 
            className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Navigation button - Previous */}
            <motion.button
              className="flex items-center justify-center w-12 h-12 text-white rounded-full bg-black/30 backdrop-blur-sm border border-white/10 hover:bg-black/50 hover:border-futuristic-blue/50 transition-colors"
              onClick={() => navigate('prev')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              variants={itemVariants}
              aria-label="Previous screenshot"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>

            {/* Phone mockup */}
            <motion.div 
              className="relative w-[280px] h-[580px] bg-black rounded-[40px] border-[8px] border-gray-800 shadow-2xl overflow-hidden"
              variants={phoneVariants}
              style={{
                boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.1), 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(52, 152, 219, 0.3)'
              }}
            >
              {/* Phone notch */}
              <div className="absolute top-0 left-0 right-0 flex justify-center z-20">
                <div className="w-40 h-6 bg-black rounded-b-xl"></div>
              </div>
              
              {/* Screen content */}
              <div className="relative w-full h-full overflow-hidden bg-gray-900">
                <AnimatePresence custom={direction} initial={false}>
                  <motion.div
                    key={currentIndex}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0"
                  >
                    <Image
                      src={appScreenshots[currentIndex].url}
                      alt={appScreenshots[currentIndex].alt}
                      width={280}
                      height={580}
                      className="object-cover w-full h-full"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
              
              {/* Home indicator */}
              <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                <div className="w-32 h-1 bg-white/30 rounded-full"></div>
              </div>
            </motion.div>

            {/* Navigation button - Next */}
            <motion.button
              className="flex items-center justify-center w-12 h-12 text-white rounded-full bg-black/30 backdrop-blur-sm border border-white/10 hover:bg-black/50 hover:border-futuristic-purple/50 transition-colors"
              onClick={() => navigate('next')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              variants={itemVariants}
              aria-label="Next screenshot"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </div>
          
          {/* Feature description */}
          <motion.div 
            className="mt-12 text-center"
            variants={itemVariants}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="max-w-md mx-auto"
              >
                <h3 className="mb-2 text-2xl font-bold text-white">
                  {appScreenshots[currentIndex].title}
                </h3>
                <p className="text-gray-300">
                  {appScreenshots[currentIndex].description}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>
          
          {/* Indicators */}
          <motion.div 
            className="flex justify-center gap-2 mt-8"
            variants={itemVariants}
          >
            {appScreenshots.map((_, index) => (
              <button
                key={`indicator-${index}`}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                  trackFeature('mobile_showcase', 'indicator_click');
                }}
                className="group p-1"
                aria-label={`Go to screenshot ${index + 1}`}
              >
                <div 
                  className={`w-8 h-1 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-gradient-to-r from-futuristic-blue to-futuristic-purple' 
                      : 'bg-gray-600 group-hover:bg-gray-400'
                  }`}
                  style={{
                    boxShadow: index === currentIndex ? '0 0 10px rgba(52, 152, 219, 0.5)' : 'none'
                  }}
                />
              </button>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MobileShowcase; 